import { useAuth } from "../context/AuthContext";
import { NavLink, useNavigate } from "react-router-dom";
import { hasModule, planLocked } from "../lib/modules";
import { useSecurity } from "../context/SecurityContext";
import { Network, MonitorCheck, MonitorX, Lock, Stamp, CreditCard } from "lucide-react";
import VersionBadge from "./VersionBadge";
import OfflineBadge from "./OfflineBadge";
import { api } from "../lib/api";
import { toast } from "sonner";
import BranchSwitcher from "./BranchSwitcher";
import PaymentReceiptNotice from "./PaymentReceiptNotice";
import ErrorBoundary from "./ErrorBoundary";
import ShiftLock, { LOCK_KEY, lockShift } from "./ShiftLock";
import ForcePasswordChange from "./ForcePasswordChange";
import QzBadge from "./QzBadge";
import HelpButton from "./HelpButton";
import TermsModal from "./TermsModal";
import LicenseExpiredGate from "./LicenseExpiredGate";
import TerminalGate from "./TerminalGate";
import {
  LayoutDashboard, ShoppingCart, Package, CalendarClock, Users2, Landmark,
  ReceiptText, RotateCcw, UserCog, Settings, KeyRound, LogOut, Menu, X, Truck, Wallet, Coins, Monitor, Send, ClipboardList, Receipt, PiggyBank, BarChart3, CheckSquare, TrendingUp, ScanLine, Tag, MessageCircle, Fingerprint, ShoppingBag
} from "lucide-react";
import { useState, useEffect } from "react";

const NAV_GROUPS = [
  { title: "", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  ]},
  { title: "Ventas", items: [
    { to: "/pos", label: "Punto de Venta", icon: ShoppingCart },
    { to: "/checador", label: "Checador de Precios", icon: ScanLine },
    { to: "/caja", label: "Mi Caja", icon: Coins },
    { to: "/sales", label: "Historial de Ventas", icon: ReceiptText },
    { to: "/returns", label: "Devoluciones", icon: RotateCcw },
    { to: "/cotizaciones", label: "Cotizaciones", icon: ClipboardList },
    { to: "/whatsapp", label: "Ventas WhatsApp", icon: MessageCircle, waOnly: true },
    { to: "/shopify", label: "Shopify", icon: ShoppingBag },
    { to: "/timbres", label: "Facturación (CFDI)", icon: Receipt },
    { to: "/tarjetas", label: "Tarjetas y monedero", icon: CreditCard },
    { to: "/cajas", label: "Cajas de Cajeros", icon: Monitor, adminOnly: true },
  ]},
  { title: "Inventario", items: [
    { to: "/inventory", label: "Inventario", icon: Package },
    { to: "/promociones", label: "Promociones", icon: Tag, adminOnly: true },
    { to: "/batches", label: "Lotes y Caducidades", icon: CalendarClock },
    { to: "/purchases", label: "Compras", icon: Truck, adminOnly: true },
    { to: "/envios", label: "Envíos Plataformas", icon: Send, adminOnly: true },
    { to: "/multiempresa", label: "Multiempresa", icon: Network, multiOnly: true },
  ]},
  { title: "Finanzas", items: [
    { to: "/bank", label: "Banco y Caja Fuerte", icon: Landmark },
    { to: "/gastos", label: "Gastos", icon: Wallet, adminOnly: true },
    { to: "/nomina", label: "Nómina", icon: Users2, adminOnly: true },
    { to: "/finanzas", label: "Préstamos e Inversión", icon: TrendingUp, adminOnly: true },
    { to: "/presupuestos", label: "Presupuestos", icon: PiggyBank },
    { to: "/reportes", label: "Reportes", icon: BarChart3, adminOnly: true },
  ]},
  { title: "Relaciones", items: [
    { to: "/customers", label: "Clientes", icon: Users2 },
  ]},
  { title: "Organización", items: [
    { to: "/tareas", label: "Tareas y Calendario", icon: CheckSquare },
    { to: "/asistencia", label: "Checador de Asistencia", icon: Fingerprint, adminOnly: true },
    { to: "/users", label: "Usuarios", icon: UserCog, adminOnly: true },
    { to: "/miplan", label: "Mi plan y licencia", icon: KeyRound, adminOnly: true },
    { to: "/settings", label: "Configuración", icon: Settings, adminOnly: true },
  ]},
];

export default function Layout({ children }) {
  const { user, logout, refresh } = useAuth();
  const { readOnly, terminalConfigured, stationId, stations, openTerminalConfig, hiddenModules, whatsappSales, cfgLoaded } = useSecurity();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locked, setLocked] = useState(() => sessionStorage.getItem(LOCK_KEY) === "1");
  useEffect(() => {
    const h = () => setLocked(sessionStorage.getItem(LOCK_KEY) === "1");
    window.addEventListener("nx-shift-lock", h);
    return () => window.removeEventListener("nx-shift-lock", h);
  }, []);
  const { autoLockMinutes } = useSecurity();
  const isMultiLic = !!user?.license?.multi_company && !!user?.license?.group_id;
  const [pendingTransfers, setPendingTransfers] = useState(0);
  const [shopifyNew, setShopifyNew] = useState(0);
  useEffect(() => {
    const f = () => api.get("/shopify/orders/count").then((r) => setShopifyNew(r.data.nuevo || 0)).catch(() => {});
    f(); const t = setInterval(f, 60000);
    window.addEventListener("nx-shopify-changed", f);
    return () => { clearInterval(t); window.removeEventListener("nx-shopify-changed", f); };
  }, [user?.business_id]);
  useEffect(() => {
    if (!isMultiLic) return;
    const f = () => api.get("/group/transfers/pending-count").then((r) => setPendingTransfers(r.data.count || 0)).catch(() => {});
    f(); const t = setInterval(f, 60000);
    window.addEventListener("nx-transfers-changed", f);
    return () => { clearInterval(t); window.removeEventListener("nx-transfers-changed", f); };
  }, [isMultiLic, user?.business_id]);
  useEffect(() => {
    if (!autoLockMinutes || user?.role === "superadmin") return;
    let t;
    const arm = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (sessionStorage.getItem(LOCK_KEY) === "1") return;
        let items = 0; try { items = JSON.parse(localStorage.getItem(`pos_cart_${user?.business_id || ""}`) || "[]").length; } catch { /* noop */ }
        lockShift(user, { auto: true, cartItems: items });
      }, autoLockMinutes * 60000);
    };
    const evs = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, arm)); };
  }, [autoLockMinutes, user?.role, user?.name]);
  const [deskCollapsed, setDeskCollapsed] = useState(false);
  const toggleSidebar = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024) setDeskCollapsed((v) => !v);
    else setMobileOpen((v) => !v);
  };
  const isSuper = user?.role === "superadmin";
  const isManager = ["admin", "manager"].includes(user?.role);
  const currentStation = stations.find((s) => s.id === stationId);
  const showTerms = !isSuper && user?.license && !user.license.terms_accepted;
  const lic = user?.license;
  const licExpired = !isSuper && lic && (lic.status !== "active" || (lic.end_date && new Date(lic.end_date) < new Date()));
  const daysLeft = lic?.end_date ? Math.ceil((new Date(lic.end_date) - new Date()) / 86400000) : null;
  const licSoon = !isSuper && !licExpired && daysLeft !== null && daysLeft <= (lic?.billing === "mensual" ? 5 : 15);

  const [renewOpen, setRenewOpen] = useState(false);
  // Recordatorio al entrar: una vez por día por equipo cuando la licencia está por vencer
  useEffect(() => {
    if (!licSoon || !lic?.business_id) return;
    const key = `lic_reminder_${lic.business_id}_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key) || localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    setRenewOpen(true);
  }, [licSoon, lic?.business_id]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("pago") && p.get("ref") && p.get("ref").startsWith("tim:")) return; // lo maneja la página de Timbres
    if (p.get("pago") && p.get("ref")) {
      api.get(`/license/pay/status?ref=${encodeURIComponent(p.get("ref"))}`).then(({ data }) => {
        if (data.status === "paid") toast.success(data.kind === "upgrade" ? `¡Pago aprobado! Tu plan ${data.license?.plan_name || ""} ya está activo.` : `¡Pago aprobado! Tu licencia quedó activa hasta el ${new Date(data.license.end_date).toLocaleDateString("es-MX")}`, { duration: 10000 });
        else toast.warning(p.get("pago") === "failure" ? "El pago no se completó. Puedes intentarlo de nuevo." : "Pago en proceso: se activará automáticamente al confirmarse.", { duration: 10000 });
        window.history.replaceState({}, "", window.location.pathname);
        if (data.status === "paid") setTimeout(() => window.location.reload(), 2500);
      }).catch(() => {});
    }
  }, []);

  const groups = isSuper
    ? [{ title: "", items: [{ to: "/licenses", label: "Licencias", icon: KeyRound }, { to: "/saas/planes", label: "Planes y distribuidores", icon: Tag }, { to: "/saas/timbres", label: "Timbres CFDI", icon: Stamp }] }]
    : NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((l) => { const key = l.to.slice(1); if (l.waOnly && !whatsappSales) return false; if (l.multiOnly && !user?.license?.multi_company) return false; if (key === "miplan") return user?.role === "admin"; if ((hiddenModules || []).includes(key) && key !== "settings") return false; return hasModule(user, key) || planLocked(user, key); }) })).filter((g) => g.items.length);

  const bizName = user?.license?.business_name || (isSuper ? "Panel SaaS" : "Mi Negocio");

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-screen w-64 shrink-0 flex flex-col sidebar-dark transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${deskCollapsed ? "lg:hidden" : ""}`}
      >
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-md bg-white grid place-items-center overflow-hidden shrink-0 p-0.5"><img src="/profesionalpos-logo.png" alt="ProfesionalPOS" className="w-full h-full object-contain" /></div>
              <div className="min-w-0">
                <div className="font-display font-extrabold text-lg leading-none tracking-tight text-white truncate">ProfesionalPOS</div>
                <div className="text-[11px] text-emerald-400 font-semibold">{isSuper ? "Super Admin" : "ERP · POS"}</div>
              </div>
            </div>
            <button onClick={toggleSidebar} data-testid="sidebar-collapse-btn" title="Ocultar menú" className="text-slate-400 hover:text-white shrink-0"><X size={20} /></button>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {groups.map((g, gi) => (
            <div key={gi} className={gi > 0 ? "pt-3" : ""}>
              {g.title && <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{g.title}</div>}
              {g.items.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${l.to.slice(1)}`}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                >
                  <l.icon size={18} />
                  <span className="flex-1">{l.label}</span>
                  {planLocked(user, l.to.slice(1)) && <Lock size={13} className="ml-auto text-amber-400" title="Disponible en un plan superior" data-testid={`nav-lock-${l.to.slice(1)}`} />}
                  {l.to === "/shopify" && shopifyNew > 0 && <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold grid place-items-center animate-pulse" title={`${shopifyNew} pedido(s) nuevo(s) de Shopify`} data-testid="nav-shopify-badge">{shopifyNew}</span>}
                  {l.to === "/multiempresa" && pendingTransfers > 0 && <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold grid place-items-center animate-pulse" title={`${pendingTransfers} traspaso(s) pendiente(s) por surtir o confirmar`} data-testid="nav-transfers-badge">{pendingTransfers}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800">
          <div className="px-2 mb-2">
            <div className="text-sm font-semibold truncate text-slate-100">{user?.name}</div>
            <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
          </div>
          <button
            onClick={() => { logout(); nav("/login"); }}
            data-testid="logout-btn"
            className="nav-link w-full text-red-400 hover:bg-red-500/10"
          >
            <LogOut size={18} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 lg:px-6 bg-white z-20">
          <div className="flex items-center gap-3">
            <button onClick={toggleSidebar} data-testid="menu-toggle" title="Mostrar/ocultar menú" className="text-slate-600 hover:text-slate-900"><Menu size={22} /></button>
            <div className="font-display font-bold text-base tracking-tight">{bizName}</div>
          </div>
          {!isSuper && (
            <div className="flex items-center gap-2">
              <BranchSwitcher />
              <OfflineBadge />
              <QzBadge />
              <HelpButton />
              <VersionBadge autoOpen />
              <button onClick={openTerminalConfig} data-testid="terminal-chip"
                className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1 rounded-full border transition-colors ${terminalConfigured ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/20" : "bg-red-500/10 border-red-500/40 text-red-600 hover:bg-red-500/20"}`}>
                {terminalConfigured ? <MonitorCheck size={13} /> : <MonitorX size={13} />}
                {terminalConfigured ? (currentStation?.name || "Terminal") : "Sin terminal"}
              </button>
              {user?.license && (
                <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono px-3 py-1 rounded-full bg-slate-500/10 border border-slate-300 text-slate-600">
                  vence {new Date(user.license.end_date).toLocaleDateString("es-MX")}
                </div>
              )}
            </div>
          )}
          {isSuper && (
            <div className="flex items-center gap-2"><VersionBadge /></div>
          )}
        </header>
        {licSoon && (
          <div className="shrink-0 bg-amber-500 text-white text-sm px-4 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="license-soon-banner">
            <span className="font-semibold">Tu licencia {lic.billing === "mensual" ? "mensual" : "anual"} vence {daysLeft <= 0 ? "hoy" : `en ${daysLeft} día${daysLeft === 1 ? "" : "s"}`} ({new Date(lic.end_date).toLocaleDateString("es-MX")}). Realiza tu pago para no interrumpir el servicio.</span>
            <button type="button" onClick={() => setRenewOpen(true)} className="h-8 px-3 rounded-lg bg-white text-amber-700 font-bold text-xs" data-testid="license-pay-now">Pagar ahora</button>
          </div>
        )}
        {readOnly && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 lg:px-6 py-2 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="readonly-banner">
            <span className="flex items-center gap-2"><MonitorX size={16} /> <b>Modo solo lectura:</b> este equipo no tiene una terminal configurada. No se pueden registrar movimientos.</span>
            <button onClick={openTerminalConfig} className="font-bold underline shrink-0" data-testid="readonly-configure-btn">Configurar terminal</button>
          </div>
        )}
        <main className="flex-1 min-h-0 p-4 lg:p-6 overflow-y-auto overflow-x-hidden"><ErrorBoundary>{children}</ErrorBoundary></main>
      </div>
      {showTerms && <TermsModal onAccept={refresh} />}
      {locked && !isSuper && <ShiftLock />}
      {!locked && user?.must_change_password && <ForcePasswordChange />}
      {licExpired && <LicenseExpiredGate license={lic} onLogout={logout} />}
      {!isSuper && !licExpired && cfgLoaded && !terminalConfigured && <TerminalGate />}
      {!isSuper && !licExpired && <PaymentReceiptNotice />}
      {renewOpen && !licExpired && <LicenseExpiredGate license={lic} onLogout={logout} preview onClose={() => setRenewOpen(false)} />}
    </div>
  );
}
